<script lang="ts">
  import { Button, Input, Select, Badge, Skeleton } from '@aatmanova/ui';
  import type { AatmanovaClient } from '@aatmanova/sdk';

  interface Props {
    client: AatmanovaClient;
    addToast: (message: string, type?: 'info' | 'success' | 'warning' | 'error') => void;
    invalidateProducts: () => void;
    searchQuery: string;
    selectedCategoryFilter: string;
    categories: any[];
    filteredProducts: any[];
    productsQueryIsLoading: boolean;
    openCreateProduct: () => void;
    openEditProduct: (product: any) => void;
    handleDeleteProduct: (id: string) => void;
    isImportOpen?: boolean;
  }

  let {
    client,
    addToast,
    invalidateProducts,
    searchQuery = $bindable(),
    selectedCategoryFilter = $bindable(),
    categories,
    filteredProducts,
    productsQueryIsLoading,
    openCreateProduct,
    openEditProduct,
    handleDeleteProduct,
    isImportOpen = $bindable(false)
  }: Props = $props();

  // Selection State
  let selectedIds = $state<string[]>([]);
  
  // Pagination State
  let currentPage = $state(1);
  let pageSize = $state(20);
  let totalPages = $derived(Math.ceil(filteredProducts.length / pageSize));
  let paginatedProducts = $derived(filteredProducts.slice((currentPage - 1) * pageSize, currentPage * pageSize));
  let allSelected = $derived(paginatedProducts.length > 0 && paginatedProducts.every(p => selectedIds.includes(p.id)));

  $effect(() => {
    if (searchQuery || selectedCategoryFilter) {
      currentPage = 1;
    }
  });

  $effect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      currentPage = totalPages;
    }
  });

  function getPageNumbers(current: number, total: number) {
    const pages: (number | string)[] = [];
    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      if (current <= 4) {
        pages.push(1, 2, 3, 4, 5, '...', total);
      } else if (current >= total - 3) {
        pages.push(1, '...', total - 4, total - 3, total - 2, total - 1, total);
      } else {
        pages.push(1, '...', current - 1, current, current + 1, '...', total);
      }
    }
    return pages;
  }

  // Bulk Actions State
  let bulkStatus = $state<'DRAFT' | 'ACTIVE' | 'ARCHIVED'>('ACTIVE');
  let bulkPricePercent = $state<number>(0);
  let isUpdatingStatus = $state(false);
  let isUpdatingPrice = $state(false);
  let isBulkDeleting = $state(false);

  // Import State
  let importStatus = $state<string>('DRAFT');
  let importDryRun = $state<boolean>(false);
  let isUploadingFile = $state(false);
  let importResult = $state<any>(null);
  let selectedFile = $state<File | null>(null);
  let importProgress = $state<{
    status: string;
    phase: string;
    current: number;
    total: number;
    percentage: number;
    message: string;
    error?: string;
    result?: any;
  } | null>(null);

  function toggleSelectProduct(id: string) {
    if (selectedIds.includes(id)) {
      selectedIds = selectedIds.filter(s => s !== id);
    } else {
      selectedIds = [...selectedIds, id];
    }
  }

  function toggleAll() {
    if (allSelected) {
      const paginatedIds = paginatedProducts.map(p => p.id);
      selectedIds = selectedIds.filter(id => !paginatedIds.includes(id));
    } else {
      const paginatedIds = paginatedProducts.map(p => p.id);
      selectedIds = [...new Set([...selectedIds, ...paginatedIds])];
    }
  }

  async function handleBulkStatusUpdate() {
    if (selectedIds.length === 0) return;
    try {
      isUpdatingStatus = true;
      await client.bulkStatusUpdate(selectedIds, bulkStatus);
      addToast(`Updated status of ${selectedIds.length} items to ${bulkStatus}`, 'success');
      selectedIds = [];
      invalidateProducts();
    } catch (err: any) {
      addToast(err.message || 'Bulk status update failed', 'error');
    } finally {
      isUpdatingStatus = false;
    }
  }

  async function handleBulkPriceUpdate() {
    if (selectedIds.length === 0 || bulkPricePercent === 0) return;
    try {
      isUpdatingPrice = true;
      await client.bulkPriceUpdate(selectedIds, bulkPricePercent);
      addToast(`Adjusted price of ${selectedIds.length} items by ${bulkPricePercent}%`, 'success');
      bulkPricePercent = 0;
      selectedIds = [];
      invalidateProducts();
    } catch (err: any) {
      addToast(err.message || 'Bulk price update failed', 'error');
    } finally {
      isUpdatingPrice = false;
    }
  }

  async function handleBulkDelete() {
    if (selectedIds.length === 0) return;
    if (!confirm(`Are you sure you want to delete ${selectedIds.length} selected items?`)) return;
    try {
      isBulkDeleting = true;
      await client.bulkDelete(selectedIds);
      addToast(`Successfully deleted ${selectedIds.length} products`, 'success');
      selectedIds = [];
      invalidateProducts();
    } catch (err: any) {
      addToast(err.message || 'Bulk deletion failed', 'error');
    } finally {
      isBulkDeleting = false;
    }
  }

  function handleFileChange(e: Event) {
    const target = e.target as HTMLInputElement;
    if (target.files && target.files.length > 0) {
      selectedFile = target.files[0];
      importResult = null;
    }
  }

  async function handleImportSubmit(e: Event) {
    e.preventDefault();
    if (!selectedFile) { addToast('Please select a file to import.', 'warning'); return; }
    
    // Generate a unique import ID
    const importId = 'import_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9);
    
    try {
      isUploadingFile = true;
      importResult = null;
      importProgress = {
        status: 'processing',
        phase: 'initializing',
        current: 0,
        total: 0,
        percentage: 0,
        message: 'Uploading file to server...',
      };

      // Call import API passing the importId
      let res;
      if (selectedFile.name.endsWith('.zip')) {
        res = await client.importZip(selectedFile, importStatus, importDryRun, importId);
      } else {
        res = await client.importProducts(selectedFile, selectedFile.name, importStatus, importDryRun, importId);
      }

      // Now we poll the status endpoint until completed or failed
      await new Promise<void>((resolve, reject) => {
        const interval = setInterval(async () => {
          try {
            const statusRes = await client.getImportStatus(importId);
            importProgress = statusRes;

            if (statusRes.status === 'completed') {
              clearInterval(interval);
              importResult = statusRes.result;
              
              if (importResult.errors && importResult.errors.length > 0) {
                addToast(`Import finished with ${importResult.failureCount} errors`, 'warning');
              } else {
                addToast(`Successfully imported ${importResult.successCount} products`, 'success');
              }
              if (!importDryRun) invalidateProducts();
              resolve();
            } else if (statusRes.status === 'failed') {
              clearInterval(interval);
              reject(new Error(statusRes.error || 'Import process failed on server'));
            }
          } catch (pollErr) {
            console.error('Polling error:', pollErr);
          }
        }, 1000);
      });

    } catch (err: any) {
      addToast(err.message || 'File import failed', 'error');
    } finally {
      isUploadingFile = false;
      importProgress = null;
    }
  }
</script>

<div class="space-y-6">

  <!-- Import Panel -->
  {#if isImportOpen}
    <div class="p-6 bg-base-100 border border-base-300 rounded-2xl shadow-xl space-y-4 animate-fadeIn">
      <div class="flex items-center justify-between pb-3 border-b border-base-300">
        <h4 class="font-bold text-base-content">Catalog Bulk Import Portal</h4>
        <button onclick={() => isImportOpen = false} class="text-xs text-base-content/50 hover:underline">Close</button>
      </div>
      <form onsubmit={handleImportSubmit} class="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
        <div class="form-control">
          <span class="text-xs font-bold text-base-content/60 mb-2 uppercase tracking-wider">Select CSV, Excel or ZIP File</span>
          <input type="file" accept=".csv,.xlsx,.zip" onchange={handleFileChange} class="file-input file-input-bordered file-input-sm w-full font-bold" required />
        </div>
        <div class="form-control">
          <span class="text-xs font-bold text-base-content/60 mb-2 uppercase tracking-wider">Default Product Status</span>
          <Select bind:value={importStatus} size="sm">
            <option value="DRAFT">DRAFT</option>
            <option value="ACTIVE">ACTIVE</option>
            <option value="ARCHIVED">ARCHIVED</option>
          </Select>
        </div>
        <div class="form-control flex flex-row items-center gap-2 pb-2 h-10">
          <input type="checkbox" id="dryrun" bind:checked={importDryRun} class="checkbox checkbox-primary checkbox-sm" />
          <label for="dryrun" class="text-xs font-bold cursor-pointer text-base-content/70 select-none">Dry Run Only</label>
        </div>
        <div class="flex gap-2">
          <Button type="submit" variant="primary" size="sm" class="flex-1 font-bold" disabled={isUploadingFile}>
            {isUploadingFile ? 'Processing...' : 'Upload & Import'}
          </Button>
        </div>
      </form>

      {#if isUploadingFile && importProgress}
        <div class="p-4 bg-base-200 border border-primary/20 rounded-xl space-y-3">
          <div class="flex items-center justify-between text-xs font-bold">
            <span class="text-primary uppercase tracking-wider">{importProgress.message || 'Processing...'}</span>
            <span class="text-base-content/50">{importProgress.percentage}%</span>
          </div>
          <div class="w-full bg-base-300 rounded-full h-2.5 overflow-hidden">
            <div class="bg-primary h-full rounded-full transition-all duration-300" style="width: {importProgress.percentage}%"></div>
          </div>
          <div class="text-[10px] text-base-content/40 font-mono">
            Phase: {importProgress.phase} | Progress: {importProgress.current} / {importProgress.total}
          </div>
        </div>
      {/if}

      {#if importResult}
        <div class="p-4 bg-base-200 border border-base-300 rounded-xl space-y-2 text-xs">
          <div class="flex items-center justify-between">
            <span class="font-bold text-base-content uppercase tracking-wider">Import Report ({importResult.dryRun ? 'DRY RUN' : 'COMMITTED'})</span>
            <div class="flex gap-3 font-semibold">
              <span class="text-success">Success: {importResult.successCount}</span>
              <span class="text-error">Failed: {importResult.failureCount}</span>
            </div>
          </div>
          {#if importResult.errors && importResult.errors.length > 0}
            <div class="max-h-36 overflow-y-auto space-y-1 font-mono bg-black/10 p-2.5 rounded-lg border border-error/20 text-error">
              {#each importResult.errors as err}
                <div>• {err}</div>
              {/each}
            </div>
          {/if}
          {#if importResult.details && importResult.details.length > 0}
            <div class="max-h-36 overflow-y-auto space-y-1 font-mono bg-black/10 p-2.5 rounded-lg text-base-content/70">
              {#each importResult.details as row}
                <div class={row.status === 'success' ? 'text-success/80' : 'text-error/80'}>
                  [{row.status.toUpperCase()}] Row {row.row}: SKU {row.sku} {row.message ? `- ${row.message}` : ''}
                </div>
              {/each}
            </div>
          {/if}
        </div>
      {/if}
    </div>
  {/if}

  <!-- Search / Filter Bar -->
  <div class="grid grid-cols-1 md:grid-cols-3 gap-4 bg-base-100 p-4 border border-base-300 rounded-xl items-center">
    <div class="form-control w-full">
      <Input type="text" placeholder="Search by SKU, name or subcategory..." bind:value={searchQuery} size="sm" />
    </div>
    <div class="form-control w-full">
      <Select bind:value={selectedCategoryFilter} size="sm">
        <option value="">All Categories</option>
        {#if categories}
          {#each categories as cat}
            <option value={cat.id}>{cat.name}</option>
          {/each}
        {/if}
      </Select>
    </div>
    <div class="flex items-center justify-end text-sm text-base-content/50 font-bold">
      {#if filteredProducts.length > 0}
        Showing {Math.min((currentPage - 1) * pageSize + 1, filteredProducts.length)}-{Math.min(currentPage * pageSize, filteredProducts.length)} of {filteredProducts.length} items
      {:else}
        Showing 0 items
      {/if}
    </div>
  </div>

  <!-- Bulk Actions Panel -->
  {#if selectedIds.length > 0}
    <div class="flex flex-col lg:flex-row items-center justify-between gap-4 p-4 bg-primary/5 border border-primary/20 rounded-2xl shadow-xl animate-slideDown">
      <div class="flex items-center gap-3">
        <div class="bg-primary text-primary-content font-black text-sm px-3 py-1.5 rounded-lg shadow-md">{selectedIds.length} selected</div>
        <span class="text-xs font-bold text-base-content/80">Batch operations ready:</span>
      </div>
      <div class="flex flex-wrap items-center gap-3">
        <div class="flex items-center gap-1.5">
          <Select bind:value={bulkStatus} size="sm" class="w-32 bg-base-100">
            <option value="ACTIVE">ACTIVE</option>
            <option value="DRAFT">DRAFT</option>
            <option value="ARCHIVED">ARCHIVED</option>
          </Select>
          <Button onclick={handleBulkStatusUpdate} disabled={isUpdatingStatus} variant="secondary" size="sm" class="font-bold">Status</Button>
        </div>
        <div class="flex items-center gap-1.5">
          <Input type="number" step="1" bind:value={bulkPricePercent} placeholder="e.g. +10" size="sm" class="w-24 bg-base-100" />
          <span class="text-sm font-bold text-base-content/50">%</span>
          <Button onclick={handleBulkPriceUpdate} disabled={isUpdatingPrice} variant="secondary" size="sm" class="font-bold">Adjust Price</Button>
        </div>
        <Button onclick={handleBulkDelete} disabled={isBulkDeleting} variant="ghost" size="sm" class="text-error border border-error/20 hover:bg-error/10 font-bold">Delete Selection</Button>
        <button onclick={() => selectedIds = []} class="text-xs font-bold text-base-content/40 hover:text-base-content/80 transition ml-2">Clear</button>
      </div>
    </div>
  {/if}

  <!-- Product Table -->
  {#if productsQueryIsLoading}
    <div class="space-y-4">
      <Skeleton height="h-12" />
      <Skeleton height="h-16" />
      <Skeleton height="h-16" />
      <Skeleton height="h-16" />
    </div>
  {:else if filteredProducts.length === 0}
    <div class="py-20 text-center border border-dashed border-base-300 rounded-xl">
      <p class="text-base-content/50 font-medium">No products match the criteria</p>
    </div>
  {:else}
    <!-- Desktop Table View -->
    <div class="hidden md:block bg-base-100 border border-base-300 rounded-xl overflow-hidden shadow-xl">
      <div class="overflow-x-auto">
        <table class="table table-zebra table-md w-full">
          <thead>
            <tr class="bg-base-200 text-xs font-semibold text-base-content/70 uppercase tracking-wider">
              <th class="w-12 text-center">
                <input type="checkbox" checked={allSelected} onclick={toggleAll} class="checkbox checkbox-primary checkbox-sm align-middle" />
              </th>
              <th>Product Details</th>
              <th>SKU / Category</th>
              <th>Price</th>
              <th>Stock</th>
              <th>Status</th>
              <th class="text-right">Actions</th>
            </tr>
          </thead>
          <tbody class="text-base-content/90">
            {#each paginatedProducts as product}
              <tr class="hover">
                <td class="text-center">
                  <input type="checkbox" checked={selectedIds.includes(product.id)} onclick={() => toggleSelectProduct(product.id)} class="checkbox checkbox-primary checkbox-sm align-middle" />
                </td>
                <td>
                  <div class="flex items-center gap-3">
                    <div class="relative w-10 h-10 flex-shrink-0">
                      {#if product.media && product.media[0]}
                        <img src={product.media[0].url} alt={product.name} class="absolute inset-0 w-full h-full rounded object-cover border border-base-300 z-10 animate-fadeIn" onerror={(e) => { e.currentTarget.style.display = 'none'; }} />
                      {/if}
                      <div class="absolute inset-0 w-full h-full rounded bg-base-200 border border-base-300 flex items-center justify-center text-base-content/40 font-bold z-0 select-none">
                        {product.name ? product.name.charAt(0).toUpperCase() : 'P'}
                      </div>
                    </div>
                    <div class="min-w-0">
                      <div class="font-bold text-base-content truncate max-w-[240px]">{product.name}</div>
                      <div class="text-[0.6875rem] text-base-content/50 truncate max-w-[160px]">{product.slug}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <div class="font-mono text-xs font-bold text-base-content/70">{product.sku}</div>
                  <div class="text-xs font-semibold text-primary/70">{product.category?.name || 'Unassigned'} {product.subcategory?.name ? `> ${product.subcategory.name}` : ''}</div>
                </td>
                <td>
                  <div class="font-bold text-accent"><span class="text-xs opacity-75 font-semibold">Rs.</span> {Number(product.offerPrice).toFixed(2)}</div>
                  {#if Number(product.retailPrice) > Number(product.offerPrice)}
                    <div class="text-[0.6875rem] text-base-content/40 line-through"><span class="text-[0.6rem] opacity-75">Rs.</span> {Number(product.retailPrice).toFixed(2)}</div>
                  {/if}
                </td>
                <td>
                  <Badge variant={product.stock >= (product.lowStockThreshold ?? 10) ? 'success' : 'error'} size="sm" class={product.stock >= (product.lowStockThreshold ?? 10) ? 'badge-outline font-bold' : 'font-bold'}>
                    {product.stock} {product.unit || 'Piece'}(s)
                  </Badge>
                </td>
                <td>
                  <Badge variant={product.status === 'ACTIVE' ? 'success' : product.status === 'DRAFT' ? 'warning' : 'neutral'} size="xs" class="badge-outline font-bold uppercase tracking-wider">
                    {product.status}
                  </Badge>
                </td>
                <td class="text-right">
                  <div class="flex items-center justify-end gap-2">
                    <a
                      href={`/products/${product.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      class="btn btn-ghost btn-xs text-info hover:bg-info/10 font-bold flex items-center justify-center h-6 min-h-6 px-2.5 rounded-lg transition-all"
                    >
                      View
                    </a>
                    <Button onclick={() => openEditProduct(product)} variant="ghost" size="xs" class="hover:bg-base-200 text-base-content font-bold">Edit</Button>
                    <Button onclick={() => handleDeleteProduct(product.id)} variant="ghost" size="xs" class="text-error hover:bg-error/10 font-bold">Delete</Button>
                  </div>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
      
      <!-- Pagination Controls (Desktop) -->
      {#if totalPages > 1}
        <div class="flex flex-col sm:flex-row items-center justify-between gap-4 bg-base-200/50 p-4 border-t border-base-300">
          <div class="text-xs font-bold text-base-content/50 uppercase tracking-wider">
            Showing {Math.min((currentPage - 1) * pageSize + 1, filteredProducts.length)}-{Math.min(currentPage * pageSize, filteredProducts.length)} of {filteredProducts.length} products
          </div>
          <div class="flex items-center gap-2">
            <button 
              type="button" 
              disabled={currentPage === 1} 
              onclick={() => currentPage = 1} 
              class="btn btn-outline btn-xs font-bold border-base-300 disabled:opacity-30 hover:bg-base-200"
            >
              First
            </button>
            <button 
              type="button" 
              disabled={currentPage === 1} 
              onclick={() => currentPage--} 
              class="btn btn-outline btn-xs font-bold border-base-300 disabled:opacity-30 hover:bg-base-200"
            >
              Prev
            </button>
            
            <div class="join">
              {#each getPageNumbers(currentPage, totalPages) as page}
                {#if page === '...'}
                  <span class="join-item btn btn-xs btn-disabled font-bold border-base-300">...</span>
                {:else}
                  <button 
                    type="button"
                    onclick={() => currentPage = Number(page)}
                    class="join-item btn btn-xs font-bold border-base-300 {currentPage === page ? 'btn-primary text-primary-content' : 'hover:bg-base-200'}"
                  >
                    {page}
                  </button>
                {/if}
              {/each}
            </div>

            <button 
              type="button" 
              disabled={currentPage === totalPages} 
              onclick={() => currentPage++} 
              class="btn btn-outline btn-xs font-bold border-base-300 disabled:opacity-30 hover:bg-base-200"
            >
              Next
            </button>
            <button 
              type="button" 
              disabled={currentPage === totalPages} 
              onclick={() => currentPage = totalPages} 
              class="btn btn-outline btn-xs font-bold border-base-300 disabled:opacity-30 hover:bg-base-200"
            >
              Last
            </button>
          </div>
        </div>
      {/if}
    </div>

    <!-- Mobile Card View -->
    <div class="md:hidden space-y-4">
      <!-- Select All Mobile Bar -->
      <div class="flex items-center justify-between bg-base-100 p-4 border border-base-300 rounded-2xl shadow-sm">
        <label class="flex items-center gap-3 cursor-pointer select-none">
          <input type="checkbox" checked={allSelected} onclick={toggleAll} class="checkbox checkbox-primary checkbox-sm align-middle" />
          <span class="text-xs font-bold text-base-content/70">Select All ({paginatedProducts.length})</span>
        </label>
        {#if selectedIds.length > 0}
          <button onclick={() => selectedIds = []} class="text-xs font-bold text-error">Clear ({selectedIds.length})</button>
        {/if}
      </div>

      <!-- Card Grid -->
      <div class="grid grid-cols-1 gap-4">
        {#each paginatedProducts as product}
          <div class="card bg-base-100 border border-base-300 rounded-2xl p-4 shadow-sm hover:border-primary/30 transition-all duration-300 space-y-3">
            <!-- Card Header (Checkbox, Status, and Actions) -->
            <div class="flex items-center justify-between gap-2">
              <div class="flex items-center gap-2">
                <input type="checkbox" checked={selectedIds.includes(product.id)} onclick={() => toggleSelectProduct(product.id)} class="checkbox checkbox-primary checkbox-xs align-middle" />
                <Badge variant={product.status === 'ACTIVE' ? 'success' : product.status === 'DRAFT' ? 'warning' : 'neutral'} size="xs" class="badge-outline font-bold uppercase tracking-wider">
                  {product.status}
                </Badge>
              </div>
              <div class="flex items-center gap-1">
                <a
                  href={`/products/${product.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="btn btn-ghost btn-xs text-info hover:bg-info/10 font-bold px-1.5 h-6 min-h-6 rounded-md text-[10px]"
                >
                  View
                </a>
                <Button onclick={() => openEditProduct(product)} variant="ghost" size="xs" class="hover:bg-base-200 text-base-content font-bold h-6 min-h-6 px-1.5 text-[10px]">Edit</Button>
                <Button onclick={() => handleDeleteProduct(product.id)} variant="ghost" size="xs" class="text-error hover:bg-error/10 font-bold h-6 min-h-6 px-1.5 text-[10px]">Delete</Button>
              </div>
            </div>

            <!-- Product Main Info (Image + Title) -->
            <div class="flex gap-3">
              <div class="relative w-12 h-12 shrink-0 rounded-xl overflow-hidden border border-base-300 bg-base-200">
                {#if product.media && product.media[0]}
                  <img src={product.media[0].url} alt={product.name} class="w-full h-full object-cover animate-fadeIn" onerror={(e) => { e.currentTarget.style.display = 'none'; }} />
                {/if}
                <div class="absolute inset-0 flex items-center justify-center text-base-content/40 font-bold text-sm select-none z-0">
                  {product.name ? product.name.charAt(0).toUpperCase() : 'P'}
                </div>
              </div>
              <div class="min-w-0 flex-1 justify-center flex flex-col">
                <h4 class="font-extrabold text-xs text-base-content leading-tight line-clamp-2">{product.name}</h4>
                <p class="text-[9px] text-base-content/50 truncate mt-0.5 font-mono">{product.sku}</p>
              </div>
            </div>

            <!-- Product Details (Category, Price, Stock) -->
            <div class="grid grid-cols-2 gap-2 pt-2.5 border-t border-base-200 text-[11px]">
              <div>
                <span class="block text-[9px] font-bold text-base-content/40 uppercase tracking-wider mb-0.5">Category</span>
                <span class="font-semibold text-base-content truncate block">
                  {product.category?.name || 'Unassigned'}
                </span>
              </div>
              <div>
                <span class="block text-[9px] font-bold text-base-content/40 uppercase tracking-wider mb-0.5">Stock Status</span>
                <Badge variant={product.stock >= (product.lowStockThreshold ?? 10) ? 'success' : 'error'} size="xs" class={product.stock >= (product.lowStockThreshold ?? 10) ? 'badge-outline font-bold' : 'font-bold'}>
                  {product.stock} {product.unit || 'Piece'}(s)
                </Badge>
              </div>
              <div class="col-span-2">
                <span class="block text-[9px] font-bold text-base-content/40 uppercase tracking-wider mb-0.5">Price</span>
                <div class="flex items-baseline gap-1.5">
                  <span class="font-black text-xs text-accent">Rs. {Number(product.offerPrice).toFixed(2)}</span>
                  {#if Number(product.retailPrice) > Number(product.offerPrice)}
                    <span class="text-[9px] text-base-content/40 line-through">Rs. {Number(product.retailPrice).toFixed(2)}</span>
                  {/if}
                </div>
              </div>
            </div>
          </div>
        {/each}
      </div>

      <!-- Pagination Controls (Mobile) -->
      {#if totalPages > 1}
        <div class="bg-base-100 border border-base-300 rounded-2xl p-4 flex items-center justify-between shadow-sm">
          <button 
            type="button" 
            disabled={currentPage === 1} 
            onclick={() => currentPage--} 
            class="btn btn-outline btn-xs font-bold border-base-300 disabled:opacity-30 hover:bg-base-200"
          >
            Prev
          </button>
          
          <div class="text-xs font-bold text-base-content">
            Page {currentPage} of {totalPages}
          </div>

          <button 
            type="button" 
            disabled={currentPage === totalPages} 
            onclick={() => currentPage++} 
            class="btn btn-outline btn-xs font-bold border-base-300 disabled:opacity-30 hover:bg-base-200"
          >
            Next
          </button>
        </div>
      {/if}
    </div>
  {/if}

</div>

<style>
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes slideDown {
    from { opacity: 0; transform: translateY(-12px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .animate-fadeIn { animation: fadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
  .animate-slideDown { animation: slideDown 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
</style>
